// src/pages/Moderation/ModerationQueueDetailsPage/ModerationQueueDetailsPage.tsx
import React, { useMemo } from 'react';
import './ModerationQueueDetailsPage.scss';

import { ReportItem } from 'redux/reducers/moderation/types';
import { onHttp } from 'untils/onHttp';
import { safeJsonParse } from 'untils/safeJsonParse';

import { decodeRestrictionValue } from 'untils/decodeRestrictions';
import { ModeratorDecisionCard } from './ui/ModeratorDecisionCard';
import { ProblemMediaCard } from './ui/ProblemMediaCard';
import { QueueDetailsHeader } from './ui/QueueDetailsHeader';
import { useQueueDetails } from './ui/useQueueDetails';
import { UserInfoCard } from './ui/UserInfoCard';

export const ModerationQueueDetailsPage: React.FC = () => {
  const { reportId, reportItem, flags, loader, btnActive, approve, reject } = useQueueDetails();

  const file: ReportItem | null = useMemo(() => {
    return safeJsonParse(reportItem?.message?.message_data || '{}');
  }, [reportItem]);

  const accessHash =
    file?.media?.photo_FLAGPHOTO?.access_hash || file?.media?.document?.access_hash;

  const previewUrl = useMemo(() => {
    if (!accessHash) return undefined;
    const isVideo = Boolean(file?.media?.document?.mime_type?.includes('video'));
    return `${onHttp()}/storage/${isVideo ? 'document' : 'photo'}/${accessHash}`;
  }, [accessHash, file]);

  if (!reportItem) {
    return (
      <div className="mq-details">
        <QueueDetailsHeader title={`Report #${reportId}`} subtitle="Not found" />
        <div className="mq-details__empty">No data for this report.</div>
      </div>
    );
  }

  const user = reportItem.user;
  const channel = reportItem.channel;
  const report = reportItem.report;

  return (
    <div className="mq-details">
      <QueueDetailsHeader
        title={`Report #${reportId}`}
        subtitle={`Created: ${report?.created_at || '—'} · Restriction: ${
          flags && decodeRestrictionValue(report?.restriction, flags.data)
        }`}
      />

      <div className="mq-details__grid">
        <div className="mq-details__left">
          <UserInfoCard
            handle={user?.username || user?.name || 'Unknown user'}
            email={reportItem.email || 'Unknown email'}
            userId={String(user?.id)}
            rating={{ value: 0, max: 5 }}
            joinYear={user?.created_at ? Number(String(user.created_at).slice(0, 4)) : 2025}
            warnings={0}
            tempBlocks={0}
            title="User"
            user
          />

          <UserInfoCard
            handle={channel?.title || channel?.username || 'Unknown channel'}
            category={channel?.category || 'No category'}
            link={channel?.link || '#'}
            userId={String(channel?.id)}
            rating={{ value: 0, max: 5 }}
            joinYear={channel?.created_at ? Number(String(channel.created_at).slice(0, 4)) : 2025}
            warnings={0}
            tempBlocks={0}
            title="Channel"
            hash={String(safeJsonParse(channel?.photo || '')?.access_hash || '')}
          />

          <ProblemMediaCard
            previewUrl={previewUrl || ''}
            filename="media_for_moderation"
            photoSizes={file?.media?.photo_FLAGPHOTO?.sizes || []}
            resolution={undefined}
            mimeLabel={
              file?.media?.photo_FLAGPHOTO ? 'Image' : file?.media?.document?.mime_type || ''
            }
            uploadedAt={report?.created_at || ''}
            complaint={report?.restriction || 0}
            flags={flags}
            hash={accessHash || ''}
          />
        </div>

        <div className="mq-details__right">
          <ModeratorDecisionCard
            loader={loader}
            btnActive={btnActive}
            report={report}
            onApprove={approve}
            onReject={reject}
            // optional meta:
            approved={report?.approved}
            moderatedAt={report?.moderated_at}
            moderatorId={report?.moderator_id}
          />
        </div>
      </div>
    </div>
  );
};

export default ModerationQueueDetailsPage;
